/* eslint-disable import/dynamic-import-chunkname */
import React, { useContext, Suspense, type PropsWithChildren } from 'react';
import type ApolloClient from 'apollo-client';
import {
	type ForgeDoc,
	type Dispatch,
	type ForgeExtensionPoints,
	type ProductEnvironment,
	type ExtensionData,
	type ExtensionPayload,
} from '@atlassian/forge-ui-types';
import { IntlProvider } from 'react-intl';
import {
	ComponentErrorBoundary,
	GenericErrorBoundary,
	IntegrationErrorHandlerBoundary,
} from '../error-boundary';
import { PortalProvider } from '../context/portal';
import { WidthProvider, RendererContext } from '../context';
import { Loader } from '../web-runtime/loader';
import { type ComponentMap, type ModalExtension } from './util';
import StyleErrorBoundary from '../error-boundary/StyleErrorBoundary';
import { type Extension } from '../web-client';
import FrameCountProvider from '../provider/FrameCountProvider';
import { parseExtensionId } from '../utils';
import { EnvironmentContext } from '../context';
import { createSrcFromExtension } from '../custom-ui/iframe/utils';
import { useSendRendererRenderedAnalytics } from '../utils/useSendRendererRenderedAnalytics';
import { ForgeExtensionPointModal } from './ForgeExtensionPointModal';
import { ForgeAppErrorMessage, shouldRenderForgeAppErrorMessage } from './errorMessages';

export interface RendererProps {
	/** Apollo client to use for all product GraphQL queries */
	client?: ApolloClient<any>;
	/* Map of component types to render functions */
	components?: (defaults: ComponentMap) => ComponentMap;
	/* Function used by components to dispatch effects */
	dispatch?: Dispatch;
	/* Error message to show the user. Set when an unexpected client-side error happens. */
	error?: string;
	/* Contains all the details for the extension*/
	extension: Extension;
	/* Static object containing context values for the extension */
	extensionData?: ExtensionData;
	/* Dynamic object containing context values for the extension */
	extensionPayload?: ExtensionPayload;
	/* Contains the module type of the extension */
	extensionType?: ForgeExtensionPoints;
	/* ForgeDoc to be rendered */
	forgeDoc?: ForgeDoc;
	/* Indicates whether the renderer is being rendered inside a custom UI parent bodied macro */
	isEmbeddedUIKitInCustomUI?: boolean;
	/* Whether a dispatched effect is pending. */
	loading?: boolean;
	/* Replace the default spinner with a custom loading component. */
	loadingComponent?: React.ReactNode;
	/* Locale to use for rendering, it used in the IntlProvider for components depends on useIntl and it is the product locale to the user */
	locale?: string;
	/* Object that stores all necessary properties for a Modal Extension */
	modalExtension?: ModalExtension;
	/* Allow integration to get notified error within the render inorder to perform custom error handling */
	onError?: (error: Error) => void;
	/* Callback for a side effect to perform on success from the consuming app */
	onSuccess?: () => void;
}

const getLoadingComponent = (loadingComponent?: React.ReactNode) => {
	if (loadingComponent) {
		// Wrapping loadingComponent in a valid react element (fragment)
		// ReactNode type is not necessarily a valid React element, unable to change this type as different extension points pass in various loadingComponent types
		return <>{loadingComponent}</>;
	}
	return <Loader />;
};

const getAppDomainName = (extension: Extension, environment: ProductEnvironment) => {
	const parsedExtension = extension && extension?.id ? parseExtensionId(extension.id) : undefined;

	if (parsedExtension) {
		const iframeSrc = createSrcFromExtension(parsedExtension.appId, extension, environment, '');
		const iframeUrl = new URL(iframeSrc);

		return iframeUrl.host;
	}
	return;
};

const RendererNextLatest = React.lazy(() =>
	/* webpackChunkName: "forge-ui-renderer-with-components" */
	import('./RendererWithComponents').then((module) => ({
		default: module.RendererNextLatest,
	})),
);

// This extra component exists so errors from the "render" function are caught in the an error boundary
// Also the error boundary requires a suspense fallback
const RendererNextWithProviders = (props: RendererProps) => {
	const { extension, forgeDoc, dispatch, locale, error } = props;

	const defaultDispatch = React.useCallback(async () => {}, []);

	const forgeReactMajorVersion = forgeDoc?.forgeReactMajorVersion;

	const environment = useContext(EnvironmentContext);
	const domainName = getAppDomainName(extension, environment);

	// true while fetching initial forgeDoc
	if (props.loading && !forgeDoc) {
		return getLoadingComponent(props.loadingComponent);
	}

	// TODO: This can be removed when to move over to the new InnerIframeV2 as the ForgeAppErrorMessage component is rendered within
	// packages/forge/forge-ui/src/bridge/methods/core.tsx
	if (error && shouldRenderForgeAppErrorMessage(error)) {
		return props.modalExtension ? (
			<ForgeExtensionPointModal modalExtension={props.modalExtension}>
				<ForgeAppErrorMessage extension={extension} error={error} />
			</ForgeExtensionPointModal>
		) : (
			<ForgeAppErrorMessage extension={extension} error={error} />
		);
	}

	return (
		<Suspense fallback={getLoadingComponent(props.loadingComponent)}>
			<GenericErrorBoundary dispatch={dispatch} extension={extension}>
				<ComponentErrorBoundary dispatch={dispatch} extension={extension}>
					<StyleErrorBoundary>
						<IntegrationErrorHandlerBoundary onError={props.onError}>
							<WidthProvider>
								<IntlProvider locale={locale ?? 'en'} defaultLocale="en">
									<PortalProvider>
										<RendererContext.Provider
											value={{
												egress: extension?.egress,
												properties: extension?.properties,
												forgeEnvironment: extension?.environmentType,
												forgeReactMajorVersion: forgeReactMajorVersion,
												appDomainName: domainName,
												extensionType: extension?.type,
											}}
										>
											<FrameCountProvider>
												<RenderTrackingWrapper>
													<RendererNextLatest {...props} dispatch={dispatch ?? defaultDispatch} />
												</RenderTrackingWrapper>
											</FrameCountProvider>
										</RendererContext.Provider>
									</PortalProvider>
								</IntlProvider>
							</WidthProvider>
						</IntegrationErrorHandlerBoundary>
					</StyleErrorBoundary>
				</ComponentErrorBoundary>
			</GenericErrorBoundary>
		</Suspense>
	);
};

const RenderTrackingWrapper: React.FC<PropsWithChildren> = ({ children }) => {
	useSendRendererRenderedAnalytics();
	return <>{children}</>;
};

export default (props: RendererProps): React.JSX.Element => {
	return <RendererNextWithProviders {...props} />;
};
